<template>
  <div>
    <!-- ── 指标（可点击切换评论 / 回复） ───────────────────── -->
    <div class="stat-row">
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.targetType === 'COMMENT' }"
        @click="switchType('COMMENT')"
      >
        <div class="stat-num is-warn">{{ pendingCommentCount || 0 }}</div>
        <div class="stat-label">待审评论</div>
        <div class="stat-sub">一级评论，别人目前看不到</div>
      </div>
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.targetType === 'REPLY' }"
        @click="switchType('REPLY')"
      >
        <div class="stat-num is-warn">{{ pendingReplyCount || 0 }}</div>
        <div class="stat-label">待审回复</div>
        <div class="stat-sub">楼中楼，通过后才进楼层</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ total }}</div>
        <div class="stat-label">当前筛选结果</div>
        <div class="stat-sub">含已通过 / 已驳回</div>
      </div>
    </div>

    <!-- ── 说明 ───────────────────────────────────────────── -->
    <div class="hint-bar audit-hint">
      <i class="el-icon-info" />
      <span>
        <b>这里只处理评论与回复。</b>开关在「系统配置 → 评论」里的<b>「评论全部先审后发」</b>，
        打开后用户发的每条评论与回复都会落到这里，通过后才对外展示 ——
        待审期间<b>只有作者本人</b>能在动态详情里看到它（带「审核中」标记），别人看不到、也不计入评论数。
        驳回后正文换成「此内容已违规」占位、<b>楼层保留</b>（否则楼下别人的回复会跟着消失）；
        对已驳回的点「恢复」即是撤销误判。评论只有文字，没有机器预审这一层。
      </span>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="query.keyword"
        :placeholder="query.targetType === 'REPLY' ? '搜索回复内容' : '搜索评论内容'"
        clearable
        prefix-icon="el-icon-search"
        class="search-input audit-search"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-select v-model="query.auditStatus" class="status-select" @change="handleSearch">
        <el-option label="待人工审核" value="REVIEW" />
        <el-option label="已通过" value="PASS" />
        <el-option label="已驳回" value="BLOCK" />
        <el-option label="全部" value="ALL" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleSearch">查询</el-button>
      <el-button class="ghost-btn" @click="resetQuery">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 条</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="loading" :data="list" style="width: 100%" :row-class-name="rowClass">
        <el-table-column label="用户" min-width="180">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <img class="owner-avatar audit-avatar" :src="row.avatar || defaultAvatar" />
              <div class="owner-info">
                <div class="owner-name">{{ row.nickname || '未设置昵称' }}</div>
                <div class="owner-school">{{ row.schoolName || '—' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="待审内容" min-width="240">
          <template slot-scope="{ row }">
            <div class="cell-main content-clamp">{{ row.content || '（空）' }}</div>
            <div v-if="row.targetType === 'REPLY'" class="cell-sub">
              回复 {{ row.replyToNickname || '——' }}
            </div>
            <div v-if="row.status === 1" class="cell-sub is-danger">
              该内容已被举报成立，正文已遮罩
            </div>
          </template>
        </el-table-column>

        <el-table-column label="语境" min-width="220">
          <template slot-scope="{ row }">
            <div class="ctx-quote">
              <div class="ctx-quote__label">
                {{ row.targetType === 'REPLY' ? '所属评论' : '所在动态' }}
              </div>
              <div class="ctx-quote__text content-clamp">
                {{ row.targetType === 'REPLY' ? (row.commentContent || '（评论已删除）') : (row.momentContent || '（无文字）') }}
              </div>
              <div v-if="row.targetType === 'REPLY' && row.momentContent" class="ctx-quote__sub content-clamp">
                动态：{{ row.momentContent }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="提交时间" width="160">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.createdAt || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="pillClass(row.auditStatus)">
              <i />{{ statusText(row.auditStatus) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click="openDetail(row)">查看</button>
              <template v-if="canReview(row)">
                <button type="button" class="act act-primary" @click="approve(row)">
                  {{ isRestoring(row) ? '恢复' : '通过' }}
                </button>
                <button
                  v-if="!isRestoring(row)"
                  type="button"
                  class="act act-danger"
                  @click="reject(row)"
                >驳回</button>
              </template>
              <span v-else class="row-done">已处理</span>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="empty-state">
            <i class="el-icon-circle-check" />
            <p v-if="query.auditStatus === 'REVIEW'">
              没有待审的{{ query.targetType === 'REPLY' ? '回复' : '评论' }}。
              想开启人工审核，去「系统配置 → 评论」打开「评论全部先审后发」
            </p>
            <p v-else>当前筛选下没有记录</p>
          </div>
        </template>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :current-page.sync="query.page"
          :page-size.sync="query.size"
          :page-sizes="[10, 20, 50]"
          @current-change="fetchList"
          @size-change="handleSearch"
        />
      </div>
    </div>

    <!-- ── 详情（一屏纵览） ───────────────────────────────── -->
    <el-drawer
      :visible.sync="drawerVisible"
      size="620px"
      custom-class="admin-drawer"
      :with-header="false"
    >
      <div class="audit-detail">
        <div class="audit-detail__head">
          <div class="audit-detail__who">
            <img class="audit-detail__avatar" :src="current.avatar || defaultAvatar" />
            <div>
              <div class="audit-detail__name">{{ current.nickname || '未设置昵称' }}</div>
              <div class="audit-detail__meta">
                {{ current.schoolName || '—' }} · {{ current.createdAt || '—' }}
              </div>
            </div>
          </div>
          <span class="status-pill" :class="pillClass(current.auditStatus)">
            <i />{{ statusText(current.auditStatus) }}
          </span>
        </div>

        <div class="audit-detail__section">
          <div class="audit-detail__label">
            {{ current.targetType === 'REPLY' ? '这条回复' : '这条评论' }}
            <span v-if="current.targetType === 'REPLY'" class="audit-detail__count">
              回复 {{ current.replyToNickname || '——' }}
            </span>
          </div>
          <div class="audit-detail__content">{{ current.content || '（空）' }}</div>
        </div>

        <div class="audit-detail__section">
          <div class="audit-detail__label">上下文</div>
          <div v-if="current.targetType === 'REPLY'" class="audit-detail__quote">
            <div class="audit-detail__quote-label">所属评论</div>
            <div class="audit-detail__quote-text">{{ current.commentContent || '（评论已删除）' }}</div>
          </div>
          <div class="audit-detail__quote">
            <div class="audit-detail__quote-label">所在动态</div>
            <div class="audit-detail__quote-text">{{ current.momentContent || '（无文字）' }}</div>
          </div>
        </div>

        <div v-if="current.auditReason || current.auditRemark" class="audit-detail__section">
          <div class="audit-detail__label">审核说明</div>
          <div class="audit-detail__reason">
            <div v-if="current.auditReason">用户可见：{{ current.auditReason }}</div>
            <div v-if="current.auditRemark">人工备注：{{ current.auditRemark }}</div>
          </div>
        </div>

        <div v-if="canReview(current)" class="audit-detail__footer">
          <el-button class="ghost-btn" @click="drawerVisible = false">关闭</el-button>
          <el-button
            v-if="!isRestoring(current)"
            type="danger"
            class="danger-btn"
            @click="reject(current)"
          >驳回</el-button>
          <el-button type="primary" class="primary-btn" @click="approve(current)">
            {{ isRestoring(current) ? '恢复展示' : '通过并展示' }}
          </el-button>
        </div>
        <div v-else class="audit-detail__footer">
          <el-button class="ghost-btn" @click="drawerVisible = false">关闭</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getCommentAuditList, approveCommentAudit, rejectCommentAudit } from '@/api/audit'

export default {
  name: 'CommentAuditPanel',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      pendingCommentCount: 0,
      pendingReplyCount: 0,
      query: {
        page: 1,
        size: 10,
        auditStatus: 'REVIEW',
        keyword: '',
        targetType: 'COMMENT'
      },
      drawerVisible: false,
      current: {},
      defaultAvatar: 'https://xdmh.xbjava.com/static/default-avatar.png'
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    fetchList() {
      this.loading = true
      getCommentAuditList({
        page: this.query.page,
        size: this.query.size,
        auditStatus: this.query.auditStatus,
        keyword: this.query.keyword,
        targetType: this.query.targetType
      })
        .then(res => {
          const data = res.data || {}
          this.list = data.list || []
          this.total = data.total || 0
          this.pendingCommentCount = data.pendingCommentCount || 0
          this.pendingReplyCount = data.pendingReplyCount || 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    switchType(type) {
      if (this.query.targetType === type) return
      this.query.targetType = type
      this.query.page = 1
      this.fetchList()
    },
    handleSearch() {
      this.query.page = 1
      this.fetchList()
    },
    resetQuery() {
      this.query = {
        page: 1,
        size: 10,
        auditStatus: 'REVIEW',
        keyword: '',
        targetType: this.query.targetType
      }
      this.fetchList()
    },
    openDetail(row) {
      this.current = row
      this.drawerVisible = true
    },
    canReview(row) {
      return row && (row.auditStatus === 'REVIEW' ||
        row.auditStatus === 'PENDING' ||
        row.auditStatus === 'BLOCK')
    },
    isRestoring(row) {
      return row && row.auditStatus === 'BLOCK'
    },
    approve(row) {
      if (!row || !row.id) return
      const restoring = this.isRestoring(row)
      const label = row.targetType === 'REPLY' ? '回复' : '评论'
      this.$confirm(
        restoring
          ? `恢复后这条${label}会重新对外展示。确认原判定有误？`
          : `通过后这条${label}会立即对外展示${row.targetType === 'REPLY' ? '，并计入楼层的回复数' : ''}。确认内容没有问题？`,
        restoring ? '恢复展示' : '通过审核',
        {
          confirmButtonText: restoring ? '确认恢复' : '确认通过',
          cancelButtonText: '取消',
          type: 'warning'
        }
      )
        .then(() => approveCommentAudit(row.targetType, row.id, {}))
        .then(() => {
          this.$message.success(restoring ? '已恢复展示' : '已通过')
          this.drawerVisible = false
          this.fetchList()
        })
        .catch(() => {})
    },
    reject(row) {
      if (!row || !row.id) return
      const label = row.targetType === 'REPLY' ? '回复' : '评论'
      this.$prompt(
        `驳回原因会推送给作者。正文会换成「此内容已违规」占位，楼层保留。`,
        `驳回${label}`,
        {
          confirmButtonText: '确认驳回',
          cancelButtonText: '取消',
          inputPlaceholder: '例如：含有明显广告或引流信息，请修改后重新发布',
          inputValidator: value => {
            if (!value || !value.trim()) return '请填写驳回原因'
            if (value.length > 100) return '原因请控制在 100 字以内'
            return true
          }
        }
      )
        .then(({ value }) => rejectCommentAudit(row.targetType, row.id, { remark: value.trim() }))
        .then(() => {
          this.$message.success('已驳回')
          this.drawerVisible = false
          this.fetchList()
        })
        .catch(() => {})
    },
    rowClass({ row }) {
      return row.auditStatus === 'REVIEW' ? 'row-pending' : ''
    },
    pillClass(status) {
      if (status === 'PASS') return 's1'
      if (status === 'BLOCK') return 's2'
      return 's0'
    },
    statusText(status) {
      if (status === 'PASS') return '已通过'
      if (status === 'BLOCK') return '已驳回'
      if (status === 'PENDING') return '待处理'
      return '待审核'
    }
  }
}
</script>

<style lang="scss" scoped>
.stat-card.is-clickable {
  cursor: pointer;
  user-select: none;
}

.stat-card.is-clickable:hover {
  border-color: var(--ink-300);
}

.stat-card.is-clickable.is-active {
  border-color: var(--rose-400);
  box-shadow: 0 0 0 3px var(--rose-50);
}

.stat-num.is-warn {
  color: var(--warn);
}

.audit-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  line-height: 1.7;
}

.audit-hint i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.audit-search {
  width: 240px;
}

.status-select {
  width: 150px;
}

.audit-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.content-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cell-sub.is-danger {
  color: var(--rose-600);
}

/* 语境引用块：待审内容才是主角，语境必须明显退后一层 */
.ctx-quote {
  border-left: 2px solid var(--ink-200);
  padding-left: 10px;
}

.ctx-quote__label {
  font-size: 11.5px;
  color: var(--ink-350);
  margin-bottom: 2px;
}

.ctx-quote__text {
  font-size: 13px;
  color: var(--ink-500);
  line-height: 1.6;
  word-break: break-word;
}

.ctx-quote__sub {
  font-size: 11.5px;
  color: var(--ink-400);
  margin-top: 2px;
}

.row-done {
  font-size: 12.5px;
  color: var(--ink-400);
}

.danger-btn {
  color: #fff;
  background: var(--rose-600);
  border-color: var(--rose-600);
}

.danger-btn:hover,
.danger-btn:focus {
  background: var(--rose-500);
  border-color: var(--rose-500);
  color: #fff;
}

/* 抽屉 */
.audit-detail {
  padding: 24px 28px 28px;
}

.audit-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--ink-100);
}

.audit-detail__who {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.audit-detail__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}

.audit-detail__name {
  font-size: 15px;
  font-weight: 500;
  color: var(--ink-900);
}

.audit-detail__meta {
  font-size: 12.5px;
  color: var(--ink-400);
  margin-top: 2px;
}

.audit-detail__section {
  margin-top: 22px;
}

.audit-detail__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-500);
  margin-bottom: 10px;
}

.audit-detail__count {
  font-weight: 400;
  color: var(--ink-400);
  margin-left: 6px;
}

.audit-detail__content {
  font-size: 14px;
  line-height: 1.8;
  color: var(--ink-900);
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--ink-100);
  border-radius: var(--r-md);
  padding: 12px 14px;
}

.audit-detail__quote {
  border-left: 2px solid var(--ink-200);
  padding-left: 12px;
  margin-bottom: 12px;
}

.audit-detail__quote-label {
  font-size: 11.5px;
  color: var(--ink-350);
  margin-bottom: 3px;
}

.audit-detail__quote-text {
  font-size: 13px;
  line-height: 1.75;
  color: var(--ink-500);
  white-space: pre-wrap;
  word-break: break-word;
}

.audit-detail__reason {
  font-size: 13px;
  line-height: 1.9;
  color: var(--ink-500);
}

.audit-detail__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid var(--ink-100);
}
</style>
